import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { schoolsApi } from "../schools/api";
import { Button, Card, Field, Input, PageTitle } from "../../components/ui";
import { QueryView } from "../../components/states";
import { ApiError } from "../../lib/api";

interface FormValues {
  name: string;
  area: string;
  loginEmail: string;
  contactName: string;
  password: string;
}

export function AdminSchoolsPage() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ["schools"], queryFn: () => schoolsApi.list() });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>();

  const mutation = useMutation({
    mutationFn: (v: FormValues) => schoolsApi.create(v),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["schools"] });
      reset();
    },
  });

  const apiErr = mutation.error instanceof ApiError ? mutation.error.message : null;

  return (
    <div className="space-y-6">
      <PageTitle title="学校アカウント" subtitle="1校につき1アカウントを発行します。" />

      <Card>
        <h3 className="mb-3 text-sm font-bold text-slate-600">新しい学校を追加</h3>
        <form className="space-y-4" onSubmit={handleSubmit((v) => mutation.mutate(v))}>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="学校名" error={errors.name?.message}>
              <Input {...register("name", { required: "学校名は必須です" })} />
            </Field>
            <Field label="エリア" error={errors.area?.message}>
              <Input {...register("area", { required: "エリアは必須です" })} />
            </Field>
            <Field label="担当者名" error={errors.contactName?.message}>
              <Input {...register("contactName", { required: "担当者名は必須です" })} />
            </Field>
            <Field label="ログイン用メール" error={errors.loginEmail?.message}>
              <Input type="email" {...register("loginEmail", { required: "メールは必須です" })} />
            </Field>
            <Field label="初期パスワード（12文字以上）" error={errors.password?.message}>
              <Input
                type="password"
                {...register("password", {
                  required: "パスワードは必須です",
                  minLength: { value: 12, message: "12文字以上にしてください" },
                })}
              />
            </Field>
          </div>
          {apiErr && <p className="text-sm font-bold text-rose-500">{apiErr}</p>}
          <div className="flex justify-end">
            <Button type="submit" disabled={mutation.isPending}>
              発行する
            </Button>
          </div>
        </form>
      </Card>

      <QueryView
        query={query}
        emptyWhen={(d) => d.items.length === 0}
        emptyMessage="まだ学校がありません。"
      >
        {(data) => (
          <div className="grid gap-3 md:grid-cols-2">
            {data.items.map((s) => (
              <Card key={s.id}>
                <h3 className="text-base font-extrabold text-brand-dark">{s.name}</h3>
                <p className="mt-1 text-xs text-slate-400">{s.area}</p>
              </Card>
            ))}
          </div>
        )}
      </QueryView>
    </div>
  );
}
